import React from 'react';
import { Card, CardContent } from '@/components/ui/Card';
import { RfqMetricsSummary } from '@/types/domain/rfq-management';
import { Layers, CheckCircle2, FileText, AlertTriangle, ShieldCheck, Send } from 'lucide-react';

interface RfqKpiCardsProps {
  metrics: RfqMetricsSummary;
  onFilterClick?: (type: string) => void;
}

export const RfqKpiCards: React.FC<RfqKpiCardsProps> = ({ metrics, onFilterClick }) => {
  const cards = [
    {
      title: 'Total RFQs',
      value: metrics.totalRfqs,
      subtitle: 'Active sourcing packages',
      icon: Layers,
      colorClass: 'text-primary bg-primary/10 border-primary/20',
      type: 'ALL',
    },
    {
      title: 'Draft Sourcing',
      value: metrics.drafts,
      subtitle: 'Configuring requirements',
      icon: FileText,
      colorClass: 'text-slate-600 bg-slate-500/10 border-slate-500/20',
      type: 'DRAFT',
    },
    {
      title: 'Open Sourcing',
      value: metrics.openRfqs,
      subtitle: 'Active bidding window',
      icon: Send,
      colorClass: 'text-blue-600 bg-blue-500/10 border-blue-500/20',
      type: 'OPEN',
    },
    {
      title: 'Responses Received',
      value: metrics.responsesReceived,
      subtitle: 'Quotation bids submitted',
      icon: CheckCircle2,
      colorClass: 'text-emerald-600 bg-emerald-500/10 border-emerald-500/20',
      type: 'RESPONSES_RECEIVED',
    },
    {
      title: 'Evaluation Ready',
      value: metrics.evaluationReady,
      subtitle: 'Handoff to Phase 15',
      icon: ShieldCheck,
      colorClass: 'text-purple-600 bg-purple-500/10 border-purple-500/20',
      type: 'EVALUATION_READY',
    },
    {
      title: 'Deadline Approaching',
      value: metrics.deadlineApproaching,
      subtitle: 'Closing within 48 hrs',
      icon: AlertTriangle,
      colorClass: 'text-amber-600 bg-amber-500/10 border-amber-500/20',
      type: 'DEADLINE_APPROACHING',
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3.5 font-poppins">
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <Card
            key={card.title}
            onClick={() => onFilterClick && onFilterClick(card.type)}
            className="rounded-2xl border border-border bg-card hover:border-primary/40 transition-all cursor-pointer shadow-2xs font-poppins"
          >
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <div className={`p-2 rounded-xl border ${card.colorClass}`}>
                  <Icon className="h-4 w-4" />
                </div>
              </div>

              <div>
                <span className="font-poppins text-xl sm:text-2xl font-extrabold tracking-tight text-foreground block truncate">
                  {card.value}
                </span>
                <span className="text-xs font-bold text-foreground leading-tight block truncate font-poppins">
                  {card.title}
                </span>
              </div>

              <p className="text-[10px] text-foreground-muted font-medium truncate pt-1 border-t border-border/40 font-poppins">
                {card.subtitle}
              </p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
